/**
 * API client for video and comment likes
 */

const API_BASE_URL = process.env.REACT_APP_API_BASE || "http://localhost:8080";

/**
 * Get auth token with force refresh support
 * @param {Object} getToken - Firebase's getIdToken function
 * @param {boolean} forceRefresh - Force token refresh to get updated claims
 * @returns {Promise<string|null>} Auth token or null
 */
const getAuthToken = async (getToken, forceRefresh = true) => {
  try {
    // ✅ FIX: Force token refresh by default to get updated email_verified claim
    // Without this, cached tokens still show email_verified: false even after verification
    const token = await getToken(forceRefresh);
    return token;
  } catch (error) {
    return null;
  }
};

/**
 * Like a video
 * @param {string} videoId - Video ID
 * @param {Function} getToken - Clerk's getToken function
 * @returns {Promise<{success: boolean, data?: {liked: boolean, likeCount: number}, error?: string}>}
 */
export const likeVideo = async (videoId, getToken) => {
  const token = await getAuthToken(getToken);
  if (!token) {
    return {
      success: false,
      error: "Authentication required",
    };
  }

  try {
    const response = await fetch(`${API_BASE_URL}/api/videos/${videoId}/like`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    const data = await response.json();
    return data;
  } catch (error) {
    return {
      success: false,
      error: "Network error",
    };
  }
};

/**
 * Unlike a video
 * @param {string} videoId - Video ID
 * @param {Function} getToken - Clerk's getToken function
 * @returns {Promise<{success: boolean, data?: {liked: boolean, likeCount: number}, error?: string}>}
 */
export const unlikeVideo = async (videoId, getToken) => {
  const token = await getAuthToken(getToken);
  if (!token) {
    return {
      success: false,
      error: "Authentication required",
    };
  }

  try {
    const response = await fetch(`${API_BASE_URL}/api/videos/${videoId}/like`, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    const data = await response.json();
    return data;
  } catch (error) {
    return {
      success: false,
      error: "Network error",
    };
  }
};

/**
 * Get like state for a video
 * @param {string} videoId - Video ID
 * @param {Function|null} getToken - Clerk's getToken function (optional)
 * @returns {Promise<{success: boolean, data?: {liked: boolean, likeCount: number}, error?: string}>}
 */
export const getVideoLikeState = async (videoId, getToken = null) => {
  try {
    const headers = {
      "Content-Type": "application/json",
    };

    // Add auth token if available (optional for this endpoint)
    if (getToken) {
      const token = await getAuthToken(getToken);
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
    }

    const response = await fetch(`${API_BASE_URL}/api/videos/${videoId}/like`, {
      method: "GET",
      headers,
    });

    const data = await response.json();
    return data;
  } catch (error) {
    return {
      success: false,
      error: "Network error",
    };
  }
};

/**
 * Like a comment
 * @param {string} commentId - Comment ID
 * @param {Function} getToken - Clerk's getToken function
 * @returns {Promise<{success: boolean, data?: {liked: boolean, likeCount: number}, error?: string}>}
 */
export const likeComment = async (commentId, getToken) => {
  const token = await getAuthToken(getToken);
  if (!token) {
    return {
      success: false,
      error: "Authentication required",
    };
  }

  try {
    const response = await fetch(
      `${API_BASE_URL}/api/comments/${commentId}/like`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
    );

    const data = await response.json();
    return data;
  } catch (error) {
    return {
      success: false,
      error: "Network error",
    };
  }
};

/**
 * Unlike a comment
 * @param {string} commentId - Comment ID
 * @param {Function} getToken - Clerk's getToken function
 * @returns {Promise<{success: boolean, data?: {liked: boolean, likeCount: number}, error?: string}>}
 */
export const unlikeComment = async (commentId, getToken) => {
  const token = await getAuthToken(getToken);
  if (!token) {
    return {
      success: false,
      error: "Authentication required",
    };
  }

  try {
    const response = await fetch(
      `${API_BASE_URL}/api/comments/${commentId}/like`,
      {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
    );

    const data = await response.json();
    return data;
  } catch (error) {
    return {
      success: false,
      error: "Network error",
    };
  }
};

/**
 * Get like state for a comment
 * @param {string} commentId - Comment ID
 * @param {Function|null} getToken - Clerk's getToken function (optional)
 * @returns {Promise<{success: boolean, data?: {liked: boolean, likeCount: number}, error?: string}>}
 */
export const getCommentLikeState = async (commentId, getToken = null) => {
  try {
    const headers = {
      "Content-Type": "application/json",
    };

    // Add auth token if available (optional for this endpoint)
    if (getToken) {
      const token = await getAuthToken(getToken);
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
    }

    const response = await fetch(
      `${API_BASE_URL}/api/comments/${commentId}/like`,
      {
        method: "GET",
        headers,
      }
    );

    const data = await response.json();
    return data;
  } catch (error) {
    return {
      success: false,
      error: "Network error",
    };
  }
};
